import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  AuthenticationPasswordsUpdateInput,
  AuthenticationScopeEnum,
  useAuthenticationPasswordsForgotMutation,
  useAuthenticationPasswordsUpdateMutation,
  UserSelfUpdateInputObject,
  useUserSelfUpdateMutation,
  WhoamiDocument,
} from 'graphql-common';
import _get from 'lodash/get';
import _pick from 'lodash/pick';
import _cloneDeep from 'lodash/cloneDeep';
import { prepareAttachedField } from '@lib/utils/prepareAttachedField';
import toast from '@lib/components/Toast/Toast';
import { getErrorMessage, getGraphQLErrors } from '@lib/utils/graphQLErrors';
import getAppUrlWithDomain from '@lib/utils/getAppUrlWithDomain';
import { APP_URLS } from 'constants/urls';
import Profile from 'components/Profile/Profile';
import { Values } from '@lib/interfaces/form';
import { useWhoamiContext } from 'hooks/useWhoamiContext';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';

const { VITE_AUTH_PLATFORM_URL: AUTH_PLATFORM } = import.meta.env;

function ProfileContainer() {
  const { t } = useTranslation();
  // Reads ProtectedRoute's single whoami query result — see useWhoamiContext.
  // The `updateMutation` cache write below reaches this through that query's
  // own cache broadcast, so no second subscription is needed here.
  const { currentUser, currentUserEmail } = useWhoamiContext();

  const { onLogoutHandler, logoutLoading }: ContextProps = useOutletContext();

  const [updateMutation, updateMutationResult] = useUserSelfUpdateMutation({
    onCompleted: () => {
      toast({ content: t('update-user-success') });
    },
    onError: (error) => {
      toast({ content: getErrorMessage(error, t) });
    },
    update: (cache, result) => {
      try {
        const cacheData = _cloneDeep(
          cache.readQuery({
            query: WhoamiDocument,
          }),
        );
        const data = _get(result, 'data.data', {});
        const existingData = _get(cacheData, 'whoami', {});
        if (existingData && data) {
          const updatedData = {
            // @ts-ignore
            ...cacheData,
            whoami: {
              ...existingData,
              ...data,
            },
          };
          cache.writeQuery({
            query: WhoamiDocument,
            data: updatedData,
          });
        }
      } catch (e) {
        console.warn(e);
      }
    },
  });

  const [sendPasswordResetMutation] = useAuthenticationPasswordsForgotMutation({
    onCompleted: (response) => {
      const message = _get(response, 'data.message', '');
      toast({ content: message });
    },
    onError: (error) => {
      toast({ content: getErrorMessage(error, t) });
    },
  });

  const [passwordResetMutation, passwordResetMutationResult] =
    useAuthenticationPasswordsUpdateMutation({
      onCompleted: () => {
        toast({ content: t('reset-password-success') });
      },
      onError: (error) => {
        toast({ content: getErrorMessage(error, t) });
      },
    });

  const { error: updateMutationError, loading: updateMutationLoading } =
    updateMutationResult;
  const graphQLErrors = getGraphQLErrors(updateMutationError);

  const {
    error: passwordResetMutationError,
    loading: passwordResetMutationLoading,
    reset: passwordResetMutationReset,
  } = passwordResetMutationResult;
  const passwordResetGraphQLErrors = getGraphQLErrors(
    passwordResetMutationError,
  );

  const onUpdateHandler = (values: Values) => {
    const attributes = _pick(values, ['lang']) as UserSelfUpdateInputObject;
    const avatarAttached = prepareAttachedField(values.avatarAttached);
    if (avatarAttached !== undefined) {
      attributes.avatarAttached = avatarAttached;
    }
    return updateMutation({
      variables: {
        input: {
          attributes,
          redirectUrl: getAppUrlWithDomain(
            APP_URLS.setup.confirmEmail.path,
            AUTH_PLATFORM,
          ),
        },
      },
    });
  };

  const onUpdatePasswordHandler = (values: Values) => {
    const attributes = _pick(values, [
      'passwordChallenge',
      'password',
      'passwordConfirmation',
    ]) as AuthenticationPasswordsUpdateInput;
    return passwordResetMutation({
      variables: {
        input: attributes,
      },
    });
  };

  const onSendPasswordResetHandler = () =>
    sendPasswordResetMutation({
      variables: {
        input: {
          email: currentUserEmail || '',
          redirectUrl: getAppUrlWithDomain(
            APP_URLS.auth.resetPassword.path,
            AUTH_PLATFORM,
          ),
          scope: AuthenticationScopeEnum.User,
        },
      },
    });

  return (
    <Profile
      currentUser={currentUser}
      graphQLErrors={graphQLErrors}
      loading={updateMutationLoading}
      logoutLoading={logoutLoading}
      onLogout={onLogoutHandler}
      onSendPasswordResetHandler={onSendPasswordResetHandler}
      onUpdateHandler={onUpdateHandler}
      onUpdatePasswordHandler={onUpdatePasswordHandler}
      passwordResetGraphQLErrors={passwordResetGraphQLErrors}
      passwordResetMutationLoading={passwordResetMutationLoading}
      passwordResetMutationReset={passwordResetMutationReset}
    />
  );
}

export default ProfileContainer;
